import { SearchX } from "lucide-react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { Button } from "@/components/ui/button"

/**
 * OKS-25 F9 — Crew no-results panel.
 *
 * Surfaces when `is_empty === false && members.length === 0` — the
 * brand has active memberships but the active filters narrow the
 * roster to zero. Distinct from `<CrewListEmptyState>` (no
 * memberships at all).
 *
 * Per UX Spec §States — No-results:
 *   - heading: "No crew members match these filters"
 *   - "Clear filters" ghost button → resets all filters to defaults
 *   - Filter rail + search remain interactive (NOT hidden); the
 *     active-filter chip rail (F7) is rendered ABOVE this panel by
 *     the page so users can dismiss filters individually.
 *
 * Locked testids:
 *   - `crew-list-no-results` — section root
 *   - `crew-list-no-results-clear` — "Clear filters" button
 */
interface CrewListNoResultsPanelProps {
  onClearAll: () => void
}

export function CrewListNoResultsPanel({
  onClearAll,
}: CrewListNoResultsPanelProps) {
  const { t } = useTranslation()
  return (
    <div data-testid="crew-list-no-results">
      <EmptyState
        icon={SearchX}
        title={t("crew.list.no_results.title")}
        actions={
          <Button
            type="button"
            variant="ghost"
            data-testid="crew-list-no-results-clear"
            onClick={onClearAll}
          >
            {t("crew.list.no_results.clear")}
          </Button>
        }
      />
    </div>
  )
}
